<template>
    <div class="main-layout__container">
        <div class="content__header">
            <div class="content-header__title">Мои ставки</div>

            <div class="content-header__background content-header__background--1-line-filters bets-list-header__background"></div>
        </div>

        <FilterPanel
            :columns="3"
            @search="onSearch"
            @reset="onReset"
        >
            <UISelect
                id="status"
                v-model="auctionsFilter.status"
                :options="statusOptions"
                label="Статус"
                multiple
            />

            <UIDatePicker
                id="from_date"
                v-model="auctionsFilter.from_date"
                label="Дата от"
                :options="{ enableTime: false, dateFormat: 'd.m.Y' }"
            />

            <UIDatePicker
                id="to_date"
                v-model="auctionsFilter.to_date"
                label="Дата до"
                :options="{ enableTime: false, dateFormat: 'd.m.Y' }"
            />

            <UISelect
                id="brand_id"
                v-model="auctionsFilterBrandId"
                :options="brandOptions"
                label="Марка"
            />

            <UISelect
                id="model_id"
                v-model="auctionsFilter.model_id"
                :options="modelOptions"
                label="Модель"
                multiple
                searchable
            />
        </FilterPanel>

        <UISearchResults
            :count="joiningAuctionsPaginationMeta?.pagination.total || 0"
            :initial-sort="selectedSort"
            :sort-options="sortOptions"
            @update:sort="onSortUpdate"
        />

        <UILoader v-if="isJoiningAuctionsLoading" class="listable" />

        <div v-else class="content__body">
            <div v-if="arJoiningAuctions.length > 0" class="bets-list__content">
                <AuctionListItem
                    v-for="auction in arJoiningAuctions"
                    :key="'auction.' + auction.id"
                    :auction="auction"
                    @updated="fetchAuctions"
                />
            </div>
            <div v-else class="bets-list__content">
                <UINotFoundData />
            </div>
        </div>

        <div class="content__footer">
            <TablePagination
                :total-pages="joiningAuctionsPaginationMeta?.pagination.last_page ?? 1"
                :current-page="joiningAuctionsPaginationMeta?.pagination.current_page ?? 1"
                :items-per-page="joiningAuctionsPaginationMeta?.pagination.per_page ?? 10"
                @page-change="changePage"
            />
        </div>
    </div>
</template>

<script setup lang="ts">
    import { ref, onMounted, onUnmounted, computed } from 'vue';

    import { SelectOption, SortOption } from '@/types/form.type.ts';
    import {
        Auction,
        AuctionStatus,
        getAuctionStatusLabel,
        InProgressAuctionBroadcastItem,
        JoiningAuctionsRequestFilter,
    } from '@/types/auction.type.ts';
    import { Model } from '@/types/model.type.ts';

    import useAuctionBroadcaster from '@/repositories/auction/useAuctionBroadcaster.ts';
    import useAuctionRepository from '@/repositories/auction/useAuctionRepository.ts';
    import useModelRepository from '@/repositories/model/useModelRepository.ts';
    import { useBetsFilterStore } from '@/stores/bets/bets-filter.ts';

    import { formatToUTC } from '@/utils/DateTimeFormatter.ts';
    import { getBrandSelectOptions } from '@/utils/SelectOptionFillers.ts';

    import UISearchResults from '@/components/ui/UISearchResults/UISearchResults.vue';

    import UIDatePicker from '@/components/ui/UIDatePicker/UIDatePicker.vue';
    import UILoader from '@/components/ui/UILoader/UILoader.vue';
    import UISelect from '@/components/ui/UISelect/UISelect.vue';
    import UINotFoundData from '@/components/ui/UINotFoundData/UINotFoundData.vue';

    import FilterPanel from '@/components/shared/FilterPanel/FilterPanel.vue';
    import TablePagination from '@/components/shared/TablePagination/TablePagination.vue';

    import AuctionListItem from '@/components/auction/AuctionListItem/AuctionListItem.vue';

    const { getAllActiveModels } = useModelRepository();
    const { arJoiningAuctions, joiningAuctionsPaginationMeta, isJoiningAuctionsLoading, getJoiningAuctions } = useAuctionRepository();
    const { listenInProgressAuctions, leaveInProgressAuctions } = useAuctionBroadcaster();
    const auctionsFilterStore = useBetsFilterStore();

    const auctionsFilter = ref<JoiningAuctionsRequestFilter>({ ...auctionsFilterStore.$state });

    const arModels = ref<Model[]>([]);
    const brandOptions = ref<SelectOption[]>(getBrandSelectOptions());
    const statusOptions = ref<SelectOption[]>([
        AuctionStatus.InProgress,
        AuctionStatus.AwaitingResult,
        AuctionStatus.Completed,
    ].map((status: AuctionStatus) => ({
        label: getAuctionStatusLabel(status),
        value: status,
    })));
    const selectedSort = ref<string>('start_date|desc');

    const auctionsFilterBrandId = computed<number | undefined>({
        get() {
            return auctionsFilter.value.brand_id === undefined || auctionsFilter.value.brand_id.length === 0
                ? undefined
                : auctionsFilter.value.brand_id[0];
        },
        set(brandId?: number) {
            auctionsFilter.value.brand_id = brandId === undefined ? undefined : [brandId];

            if (brandId !== undefined) {
                auctionsFilter.value.model_id = undefined;
            }
        },
    });

    const modelOptions = computed<SelectOption[]>(() => {
        return arModels.value
            .filter((model: Model) => {
                if (auctionsFilter.value.brand_id === undefined) {
                    return true;
                }

                return auctionsFilter.value.brand_id.includes(model.brand_id);
            })
            .map((model: Model) => ({
                label: model.name,
                value: model.id,
            }));
    });

    const sortOptions: SortOption[] = [
        { label: 'по дате начала аукциона (1-9)', value: 'start_date|asc' },
        { label: 'по дате начала аукциона (9-1)', value: 'start_date|desc' },
        { label: 'по дате окончания аукциона (1-9)', value: 'end_date|asc' },
        { label: 'по дате окончания аукциона (9-1)', value: 'end_date|desc' },
        { label: 'по стартовой цене аукциона (1-9)', value: 'start_price|asc' },
        { label: 'по стартовой цене аукциона (9-1)', value: 'start_price|desc' },
    ];

    function onSearch(): void {
        auctionsFilter.value.page = 1;
        fetchAuctions();
    }

    function onReset(): void {
        auctionsFilter.value = {
            brand_id: undefined,
            model_id: undefined,
            status: [AuctionStatus.InProgress],
            from_date: undefined,
            to_date: undefined,
            page: 1,
            sort: auctionsFilter.value.sort,
        };

        fetchAuctions();
    }

    function changePage(page: number): void {
        auctionsFilter.value.page = page;
        fetchAuctions();
    }

    function onSortUpdate(newSort: string): void {
        const parts = newSort.split('|');

        if (parts.length !== 2) {
            return;
        }

        const [key, dir] = parts as [string, 'asc' | 'desc'];

        auctionsFilter.value.sort = [{ [key]: dir }];
        fetchAuctions();
    }

    function onInProgressAuctionsBroadcast(auctions: InProgressAuctionBroadcastItem[]): void {
        auctions.forEach((auctionInfo: InProgressAuctionBroadcastItem) => {
            const auction = arJoiningAuctions.value.find((auction: Auction) => auction.id === auctionInfo.lot_id);
            if (auction === undefined) {
                return;
            }

            auction.broadcastInfo = auctionInfo;
        });
    }

    function mapAuctionsFilterToRequestFilter(): JoiningAuctionsRequestFilter {
        const requestFilter: JoiningAuctionsRequestFilter = {
            page: auctionsFilter.value.page,
            sort: auctionsFilter.value.sort,
        };

        if (auctionsFilter.value.brand_id !== undefined && auctionsFilter.value.brand_id.length > 0) {
            requestFilter.brand_id = auctionsFilter.value.brand_id;
        }

        if (auctionsFilter.value.model_id !== undefined && auctionsFilter.value.model_id.length > 0) {
            requestFilter.model_id = auctionsFilter.value.model_id;
        }

        if (auctionsFilter.value.status !== undefined && auctionsFilter.value.status.length > 0) {
            requestFilter.status = auctionsFilter.value.status;
        }

        if (auctionsFilter.value.from_date !== undefined && auctionsFilter.value.from_date.length > 0) {
            requestFilter.from_date = formatToUTC(auctionsFilter.value.from_date, {
                fromFormat: 'dd.LL.yyyy',
                toFormat: 'yyyy-LL-dd',
                withTime: false,
            });
        }

        if (auctionsFilter.value.to_date !== undefined && auctionsFilter.value.to_date.length > 0) {
            requestFilter.to_date = formatToUTC(auctionsFilter.value.to_date, {
                fromFormat: 'dd.LL.yyyy',
                toFormat: 'yyyy-LL-dd',
                withTime: false,
            });
        }

        return requestFilter;
    }

    function fetchFilterOptions(): void {
        getAllActiveModels().then((models: Model[]) => {
            arModels.value = models;
        });
    }

    function fetchAuctions(): void {
        auctionsFilterStore.$patch({ ...auctionsFilter.value });
        getJoiningAuctions(mapAuctionsFilterToRequestFilter());
    }

    onMounted(() => {
        fetchAuctions();
        fetchFilterOptions();
        listenInProgressAuctions(onInProgressAuctionsBroadcast);
    });

    onUnmounted(() => {
        leaveInProgressAuctions();
    });
</script>

<style scoped lang="scss">
    @use './BetsList.scss' as *;
</style>
